/**
 * batch-63 cursusplanning (FO-468) - cursusaanvraag aan de opleider.
 *
 * Gedeelde bron voor API en backoffice: statussen + labels, de mailteksten
 * (bovenop de FO-019-aanmeldtemplate: zelfde `[token]`-vorm, zelfde
 * cursist-blok, zelfde per-BV-config), de Zod-schema's (API `.safeParse` én
 * client-side validatie vóór verzenden) en de DTO's.
 *
 * Een cursusaanvraag is NIET de FO-004-`OpleidingAanvraag` (het interne
 * verzoek van een medewerker aan HR): die houdt zijn eigen portal-statussen.
 */

import { z } from 'zod'
import { idParam } from './id-validators'
import {
  resolveCursistBlok,
  type AanmeldAlgemeenPlaceholders,
  type AanmeldCursistPlaceholders,
  type AanmeldCursistPreviewDTO,
} from './opleiding-aanmelding'
import type { WerkbonConflictDTO } from './werkbonnen'

// ─── Statussen ───────────────────────────────────────────────────────────────

export const CURSUS_AANVRAAG_STATUSSEN = [
  'AANGEVRAAGD',
  'TE_BEOORDELEN',
  'BEVESTIGD',
  'AFGEWEZEN',
  'GEANNULEERD',
] as const
export type CursusAanvraagStatusKey = (typeof CURSUS_AANVRAAG_STATUSSEN)[number]

export const CURSUS_AANVRAAG_STATUS_LABELS: Record<CursusAanvraagStatusKey, string> = {
  AANGEVRAAGD: 'Aangevraagd',
  TE_BEOORDELEN: 'Antwoord beoordelen',
  BEVESTIGD: 'Bevestigd',
  AFGEWEZEN: 'Afgewezen',
  GEANNULEERD: 'Geannuleerd',
}

export const CURSUS_AANVRAAG_STATUS_UITLEG: Record<CursusAanvraagStatusKey, string> = {
  AANGEVRAAGD: 'Aanvraag verstuurd; de cursus staat onder voorbehoud in de planning.',
  TE_BEOORDELEN: 'De opleider heeft geantwoord; er is een besluit van de planner nodig.',
  BEVESTIGD: 'Bevestigd door de opleider; de planning is definitief.',
  AFGEWEZEN: 'Afgewezen door de opleider; de dag is weer vrij. Pas de aanvraag aan of annuleer.',
  GEANNULEERD: 'Ingetrokken; de planning is opgeruimd.',
}

/** Statussen waarin de aanvraag nog "open" is (werkvoorraad). */
export const CURSUS_AANVRAAG_OPEN_STATUSSEN = [
  'AANGEVRAAGD',
  'TE_BEOORDELEN',
  'AFGEWEZEN',
] as const satisfies readonly CursusAanvraagStatusKey[]

export const CURSUS_AANVRAAG_KANALEN = ['MAIL', 'HANDMATIG', 'INTERN'] as const
export type CursusAanvraagKanaalKey = (typeof CURSUS_AANVRAAG_KANALEN)[number]

export const CURSUS_AANVRAAG_KANAAL_LABELS: Record<CursusAanvraagKanaalKey, string> = {
  MAIL: 'Automatische mail',
  HANDMATIG: 'Zelf verstuurd',
  INTERN: 'Interne opleider',
}

export const CURSUS_AANVRAAG_MAIL_SOORTEN = [
  'AANVRAAG',
  'AANPASSING',
  'HERINNERING',
  'ANNULERING',
] as const
export type CursusAanvraagMailSoortKey = (typeof CURSUS_AANVRAAG_MAIL_SOORTEN)[number]

export const CURSUS_AANVRAAG_MAIL_SOORT_LABELS: Record<CursusAanvraagMailSoortKey, string> = {
  AANVRAAG: 'Aanvraag',
  AANPASSING: 'Gewijzigde aanvraag',
  HERINNERING: 'Herinnering',
  ANNULERING: 'Annulering',
}

export const CURSUS_AANVRAAG_REPLY_STATUSSEN = [
  'NIEUW',
  'VERWERKT',
  'TE_BEOORDELEN',
  'AFGEHANDELD',
  'NIET_GEKOPPELD',
  'GENEGEERD',
  'FOUT',
  'QUARANTAINE',
] as const
export type CursusAanvraagReplyStatusKey = (typeof CURSUS_AANVRAAG_REPLY_STATUSSEN)[number]

export const CURSUS_AANVRAAG_REPLY_STATUS_LABELS: Record<CursusAanvraagReplyStatusKey, string> = {
  NIEUW: 'Nog niet verwerkt',
  VERWERKT: 'Verwerkt',
  TE_BEOORDELEN: 'Te beoordelen',
  AFGEHANDELD: 'Afgehandeld',
  NIET_GEKOPPELD: 'Niet gekoppeld',
  GENEGEERD: 'Genegeerd',
  FOUT: 'Fout bij verwerken',
  QUARANTAINE: 'In quarantaine',
}

/** Reply-statussen die een mens vragen (werkvoorraad "opleidermails"). */
export const CURSUS_AANVRAAG_REPLY_WERKVOORRAAD = [
  'TE_BEOORDELEN',
  'NIET_GEKOPPELD',
  'FOUT',
  'QUARANTAINE',
] as const satisfies readonly CursusAanvraagReplyStatusKey[]

export const CURSUS_AANVRAAG_REPLY_OORDELEN = [
  'BEVESTIGD',
  'AFGEWEZEN',
  'ALTERNATIEF',
  'ONDUIDELIJK',
] as const
export type CursusAanvraagReplyOordeelKey = (typeof CURSUS_AANVRAAG_REPLY_OORDELEN)[number]

export const CURSUS_AANVRAAG_REPLY_OORDEEL_LABELS: Record<CursusAanvraagReplyOordeelKey, string> = {
  BEVESTIGD: 'Bevestigd',
  AFGEWEZEN: 'Afgewezen',
  ALTERNATIEF: 'Alternatief voorgesteld',
  ONDUIDELIJK: 'Onduidelijk',
}

export const CURSUS_AANVRAAG_MATCH_WIJZEN = ['HEADER', 'REFERENTIE', 'AFZENDER', 'HANDMATIG'] as const
export type CursusAanvraagMatchWijzeKey = (typeof CURSUS_AANVRAAG_MATCH_WIJZEN)[number]

export const CURSUS_AANVRAAG_MATCH_WIJZE_LABELS: Record<CursusAanvraagMatchWijzeKey, string> = {
  HEADER: 'Antwoord op onze mail',
  REFERENTIE: 'Referentie in het onderwerp',
  AFZENDER: 'Afzender (controleren)',
  HANDMATIG: 'Handmatig gekoppeld',
}

export const CURSUS_AANVRAAG_REVIEW_REDENEN = [
  'AFWIJKENDE_PLANNING',
  'DATUM_ONBEKEND',
  'ALTERNATIEF',
  'ONDUIDELIJK',
  'LAGE_ZEKERHEID',
  'VREEMDE_AFZENDER',
  'ZWAKKE_MATCH',
  'LEGE_REPLY',
  'NA_BESLUIT',
  'OPEN_BEOORDELING',
] as const
export type CursusAanvraagReviewRedenKey = (typeof CURSUS_AANVRAAG_REVIEW_REDENEN)[number]

export const CURSUS_AANVRAAG_REVIEW_REDEN_LABELS: Record<CursusAanvraagReviewRedenKey, string> = {
  AFWIJKENDE_PLANNING: 'Bevestigd, maar anders dan gepland',
  DATUM_ONBEKEND: 'Bevestigd zonder (alle) cursusdagen',
  ALTERNATIEF: 'Alternatief voorgesteld',
  ONDUIDELIJK: 'Onduidelijk antwoord',
  LAGE_ZEKERHEID: 'Lage zekerheid',
  VREEMDE_AFZENDER: 'Onbekende afzender',
  ZWAKKE_MATCH: 'Alleen gekoppeld op afzender',
  LEGE_REPLY: 'Geen tekst in het antwoord',
  NA_BESLUIT: 'Antwoord na een besluit',
  OPEN_BEOORDELING: 'Er stond al een antwoord open',
}

/** Audit-acties op een cursusaanvraag (vrije string in de DB, dit is de bron). */
export const CURSUS_AANVRAAG_AUDIT_ACTIES = [
  'AANGEVRAAGD',
  'MAIL_VERZONDEN',
  'MAIL_MISLUKT',
  'HANDMATIG_VERSTUURD',
  'HERINNERING',
  'SIGNAAL_GEEN_ANTWOORD',
  'REPLY_ONTVANGEN',
  'TE_BEOORDELEN',
  'BEVESTIGD',
  'AFGEWEZEN',
  'AANGEPAST',
  'GEANNULEERD',
  'REPLY_AFGEHANDELD',
  'VOORKEURSDATUM_BEREIKT',
  'PLANNING_BIJGEWERKT',
] as const
export type CursusAanvraagAuditActie = (typeof CURSUS_AANVRAAG_AUDIT_ACTIES)[number]

export const CURSUS_AANVRAAG_AUDIT_LABELS: Record<CursusAanvraagAuditActie, string> = {
  AANGEVRAAGD: 'Aanvraag aangemaakt',
  MAIL_VERZONDEN: 'Mail naar de opleider verstuurd',
  MAIL_MISLUKT: 'Mail naar de opleider mislukt',
  HANDMATIG_VERSTUURD: 'Mailtekst gekopieerd (zelf versturen)',
  HERINNERING: 'Herinnering verstuurd',
  SIGNAAL_GEEN_ANTWOORD: 'Signaal: nog geen antwoord',
  REPLY_ONTVANGEN: 'Antwoord van de opleider ontvangen',
  TE_BEOORDELEN: 'Antwoord ter beoordeling',
  BEVESTIGD: 'Bevestigd',
  AFGEWEZEN: 'Afgewezen',
  AANGEPAST: 'Aanvraag aangepast',
  GEANNULEERD: 'Geannuleerd',
  REPLY_AFGEHANDELD: 'Antwoord afgehandeld',
  VOORKEURSDATUM_BEREIKT: 'Voorkeursdatum bereikt zonder bevestiging',
  PLANNING_BIJGEWERKT: 'Planning bijgewerkt',
}

// ─── Mailteksten (FO-019-template-tabel, eigen keys) ─────────────────────────

/** Rij-key in `OpleidingAanmeldInstelling` per soort mail. */
export const CURSUS_AANVRAAG_SJABLOON_KEYS: Record<CursusAanvraagMailSoortKey, string> = {
  AANVRAAG: 'cursusaanvraag',
  AANPASSING: 'cursusaanvraag-aanpassing',
  HERINNERING: 'cursusaanvraag-herinnering',
  ANNULERING: 'cursusaanvraag-annulering',
}

/** Placeholders die de aanvraagmails bovenop de FO-019-placeholders kennen. */
export const CURSUS_AANVRAAG_PLACEHOLDERS = [
  '[referentie]',
  '[locatie]',
  '[voorkeursdatum]',
  '[tweede_voorkeur_regel]',
  '[zo_snel_mogelijk_regel]',
  '[tijden]',
  '[aantal_dagen]',
  '[cursusdagen]',
  '[cursusdagen_regel]',
  '[aantal_deelnemers]',
  '[toelichting_regel]',
  '[geplande_datum]',
  '[verzonden_datum]',
  '[annuleringsreden_regel]',
  '[geboortedatum_regel]',
] as const

export const CURSUS_AANVRAAG_PLACEHOLDER_LABELS: Record<string, string> = {
  '[referentie]': 'Aanvraagreferentie (CA-jjjj-nnnn)',
  '[locatie]': 'Gekozen locatie met adres',
  '[voorkeursdatum]': 'Voorkeursdatum (eerste cursusdag)',
  '[tweede_voorkeur_regel]': 'Regel met de tweede voorkeur (leeg als er geen is)',
  '[zo_snel_mogelijk_regel]': 'Regel "eerder mag ook" (leeg als niet gekozen)',
  '[tijden]': 'Gewenste tijden, of "volgens uw rooster"',
  '[aantal_dagen]': 'Aantal cursusdagen',
  '[cursusdagen]': 'Alle cursusdagen onder elkaar',
  '[cursusdagen_regel]': 'Regel met alle cursusdagen (leeg bij een cursus van één dag)',
  '[aantal_deelnemers]': 'Aantal deelnemers',
  '[toelichting_regel]': 'Toelichting van de planner (leeg als er geen is)',
  '[geplande_datum]': 'De datum zoals nu gepland',
  '[verzonden_datum]': 'Datum waarop de aanvraag verstuurd is',
  '[annuleringsreden_regel]': 'Reden van annulering (leeg als er geen is)',
  '[geboortedatum_regel]':
    'Per cursist: regel met de geboortedatum, alleen als de opleider die vereist',
}

export const DEFAULT_CURSUS_AANVRAAG_CURSIST_BLOK = `[voorletters] [achternaam] ([voornaam])
[geboortedatum_regel]`

export interface CursusAanvraagSjabloon {
  onderwerpSjabloon: string
  bodySjabloon: string
  cursistBlokSjabloon: string
}

export const DEFAULT_CURSUS_AANVRAAG_SJABLONEN: Record<CursusAanvraagMailSoortKey, CursusAanvraagSjabloon> = {
  AANVRAAG: {
    onderwerpSjabloon: 'Aanvraag cursus [cursusnaam] - [bedrijfsnaam]',
    bodySjabloon: `Geachte heer/mevrouw,

Namens [bedrijfsnaam] willen wij graag de volgende deelnemer(s) inschrijven voor de cursus "[cursusnaam]".

Gewenste locatie: [locatie]
Voorkeursdatum: [voorkeursdatum] ([tijden])
[tweede_voorkeur_regel]
[zo_snel_mogelijk_regel]
Aantal dagen: [aantal_dagen]
[cursusdagen_regel]

Deelnemers ([aantal_deelnemers]):
[cursisten]

[toelichting_regel]

Wilt u de inschrijving bevestigen, of een andere datum voorstellen als de voorkeursdatum niet mogelijk is? Graag uw antwoord als reactie op deze mail, met de referentie [referentie] in het onderwerp.

De factuur kan naar [factuuradres]. Voor vragen kunt u contact opnemen met [contactpersoon] via [contact_email].

Met vriendelijke groet,
[contactpersoon]
[bedrijfsnaam]`,
    cursistBlokSjabloon: DEFAULT_CURSUS_AANVRAAG_CURSIST_BLOK,
  },
  AANPASSING: {
    onderwerpSjabloon: 'Gewijzigde aanvraag cursus [cursusnaam] - [bedrijfsnaam]',
    bodySjabloon: `Geachte heer/mevrouw,

Wij passen onze aanvraag met referentie [referentie] voor de cursus "[cursusnaam]" aan. Dit zijn de gewijzigde gegevens:

Gewenste locatie: [locatie]
Voorkeursdatum: [voorkeursdatum] ([tijden])
[tweede_voorkeur_regel]
[zo_snel_mogelijk_regel]
Aantal dagen: [aantal_dagen]
[cursusdagen_regel]

Deelnemers ([aantal_deelnemers]):
[cursisten]

[toelichting_regel]

Wilt u de inschrijving op deze gegevens bevestigen? Graag als reactie op deze mail, met de referentie [referentie] in het onderwerp.

Met vriendelijke groet,
[contactpersoon]
[bedrijfsnaam]`,
    cursistBlokSjabloon: DEFAULT_CURSUS_AANVRAAG_CURSIST_BLOK,
  },
  HERINNERING: {
    onderwerpSjabloon: 'Herinnering: aanvraag cursus [cursusnaam] - [bedrijfsnaam]',
    bodySjabloon: `Geachte heer/mevrouw,

Op [verzonden_datum] hebben wij namens [bedrijfsnaam] een aanvraag gestuurd voor de cursus "[cursusnaam]" (referentie [referentie]). Wij hebben nog geen bevestiging ontvangen.

Gewenste locatie: [locatie]
Voorkeursdatum: [voorkeursdatum] ([tijden])
[cursusdagen_regel]
[tweede_voorkeur_regel]

Deelnemers ([aantal_deelnemers]):
[cursisten]

Wilt u de inschrijving bevestigen of een andere datum voorstellen? Graag als reactie op deze mail, met de referentie [referentie] in het onderwerp.

Met vriendelijke groet,
[contactpersoon]
[bedrijfsnaam]`,
    cursistBlokSjabloon: DEFAULT_CURSUS_AANVRAAG_CURSIST_BLOK,
  },
  ANNULERING: {
    onderwerpSjabloon: 'Annulering cursus [cursusnaam] - [bedrijfsnaam]',
    bodySjabloon: `Geachte heer/mevrouw,

Namens [bedrijfsnaam] annuleren wij de aanvraag met referentie [referentie] voor de cursus "[cursusnaam]" op [geplande_datum] bij [locatie].
[cursusdagen_regel]

Het gaat om:
[cursisten]

[annuleringsreden_regel]

Wilt u de annulering bevestigen?

Met vriendelijke groet,
[contactpersoon]
[bedrijfsnaam]`,
    cursistBlokSjabloon: DEFAULT_CURSUS_AANVRAAG_CURSIST_BLOK,
  },
}

/** Waarden voor de aanvraag-specifieke placeholders (al geformatteerd). */
export interface CursusAanvraagMailPlaceholders {
  referentie: string
  locatie: string
  voorkeursdatum: string
  /** Leeg = de hele regel verdwijnt. */
  tweedeVoorkeurRegel: string
  zoSnelMogelijkRegel: string
  tijden: string
  aantalDagen: string
  cursusdagen: string
  /** "Cursusdagen: ..." bij meer dan één dag; leeg = de regel verdwijnt. */
  cursusdagenRegel: string
  aantalDeelnemers: string
  toelichtingRegel: string
  geplandeDatum: string
  verzondenDatum: string
  annuleringsredenRegel: string
}

export interface CursusAanvraagCursistPlaceholders extends AanmeldCursistPlaceholders {
  /** "Geboortedatum: dd-MM-jjjj" of leeg (opleider vereist hem niet / niet bekend). */
  geboortedatumRegel: string
}

const REGEL_TOKENS: Array<[string, keyof CursusAanvraagMailPlaceholders]> = [
  ['[tweede_voorkeur_regel]', 'tweedeVoorkeurRegel'],
  ['[zo_snel_mogelijk_regel]', 'zoSnelMogelijkRegel'],
  ['[cursusdagen_regel]', 'cursusdagenRegel'],
  ['[toelichting_regel]', 'toelichtingRegel'],
  ['[annuleringsreden_regel]', 'annuleringsredenRegel'],
]

const WAARDE_TOKENS: Array<[string, keyof CursusAanvraagMailPlaceholders]> = [
  ['[referentie]', 'referentie'],
  ['[locatie]', 'locatie'],
  ['[voorkeursdatum]', 'voorkeursdatum'],
  ['[tijden]', 'tijden'],
  ['[aantal_dagen]', 'aantalDagen'],
  ['[cursusdagen]', 'cursusdagen'],
  ['[aantal_deelnemers]', 'aantalDeelnemers'],
  ['[geplande_datum]', 'geplandeDatum'],
  ['[verzonden_datum]', 'verzondenDatum'],
]

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Een lege `_regel`-token verdwijnt MET zijn regel (anders blijven er gaten in
 * de mail); een gevulde vervangt alleen de token.
 */
function vervangRegel(tekst: string, token: string, waarde: string): string {
  if (waarde.trim() === '') {
    const regel = new RegExp(`^[ \\t]*${escapeRegex(token)}[ \\t]*(?:\\r?\\n|$)`, 'gm')
    return tekst.replace(regel, '').split(token).join('')
  }
  return tekst.split(token).join(waarde)
}

function resolveAlgemeen(tekst: string, a: AanmeldAlgemeenPlaceholders): string {
  return tekst
    .split('[cursusnaam]').join(a.cursusnaam)
    .split('[bedrijfsnaam]').join(a.bedrijfsnaam)
    .split('[contactpersoon]').join(a.contactpersoon)
    .split('[factuuradres]').join(a.factuuradres)
    .split('[contact_email]').join(a.contactEmail)
}

/** Vul de aanvraag-placeholders + de algemene FO-019-placeholders in. */
export function resolveCursusAanvraagTekst(
  sjabloon: string,
  algemeen: AanmeldAlgemeenPlaceholders,
  aanvraag: CursusAanvraagMailPlaceholders,
): string {
  let tekst = sjabloon
  for (const [token, veld] of REGEL_TOKENS) tekst = vervangRegel(tekst, token, aanvraag[veld])
  for (const [token, veld] of WAARDE_TOKENS) tekst = tekst.split(token).join(aanvraag[veld])
  return resolveAlgemeen(tekst, algemeen)
}

/** Eén cursist-blok (FO-019-tokens + de geboortedatum-regel). */
export function resolveCursusAanvraagCursistBlok(
  sjabloon: string,
  c: CursusAanvraagCursistPlaceholders,
): string {
  return resolveCursistBlok(vervangRegel(sjabloon, '[geboortedatum_regel]', c.geboortedatumRegel), c)
}

/**
 * Bouw onderwerp + body. Volgorde zoals FO-019: eerst de cursist-blokken (hun
 * persoonswaarden worden nooit als token gelezen), dan het invoegpunt, dan de
 * aanvraag- en algemene tokens. Drie of meer regeleindes worden er twee.
 * Het referentie-token in het onderwerp zet de API er zelf achter
 * (`onderwerpMetReferentie`), zodat een bewerkt onderwerp hem nooit verliest.
 */
export function resolveCursusAanvraagMail(opts: {
  sjabloon: CursusAanvraagSjabloon
  algemeen: AanmeldAlgemeenPlaceholders
  aanvraag: CursusAanvraagMailPlaceholders
  cursisten: CursusAanvraagCursistPlaceholders[]
}): { onderwerp: string; body: string } {
  const blokken = opts.cursisten
    .map((c) => resolveCursusAanvraagCursistBlok(opts.sjabloon.cursistBlokSjabloon, c).trim())
    .join('\n\n')
  const metCursisten = opts.sjabloon.bodySjabloon.split('[cursisten]').join(blokken)
  const body = resolveCursusAanvraagTekst(metCursisten, opts.algemeen, opts.aanvraag)
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  const onderwerp = resolveCursusAanvraagTekst(opts.sjabloon.onderwerpSjabloon, opts.algemeen, opts.aanvraag)
    .replace(/\s+/g, ' ')
    .trim()
  return { onderwerp, body }
}

const DAG_LANG = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

/** `2026-10-14` -> `woensdag 14 oktober 2026` (de dag is een NL-kalenderdag). */
export function formatCursusDagLang(ymd: string): string {
  return DAG_LANG.format(new Date(`${ymd}T00:00:00.000Z`))
}

// ─── Zod-schema's ────────────────────────────────────────────────────────────

/** Een echte kalenderdag (geen 30 februari). */
function isKalenderdag(s: string): boolean {
  const d = new Date(`${s}T00:00:00.000Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

const dagSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige datum')
  .refine(isKalenderdag, 'Kies een bestaande datum')

/** Expliciete cursusdagen (bijvoorbeeld zoals de opleider ze bevestigde). */
const dagenSchema = z
  .array(dagSchema)
  .min(1, 'Kies minimaal één cursusdag')
  .max(20, 'Maximaal 20 cursusdagen')
  .refine((d) => new Set(d).size === d.length, 'Elke cursusdag mag maar één keer voorkomen')

/** Bijlagen bij de mail aan de opleider (eerst los geüpload). */
const bijlageIdsSchema = z.array(idParam('bijlageId')).max(5, 'Maximaal 5 bijlagen')
const tijdSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik uu:mm (bijvoorbeeld 08:30)')

/** Tijden: allebei of geen van beide, en de eindtijd na de begintijd. */
function checkTijden(
  val: { startTijd?: string | null; eindTijd?: string | null },
  ctx: z.RefinementCtx,
): void {
  const start = val.startTijd ?? null
  const eind = val.eindTijd ?? null
  if ((start === null) !== (eind === null)) {
    ctx.addIssue({
      code: 'custom',
      path: [start === null ? 'startTijd' : 'eindTijd'],
      message: 'Vul zowel een begin- als een eindtijd in, of laat beide leeg',
    })
    return
  }
  if (start !== null && eind !== null && eind <= start) {
    ctx.addIssue({ code: 'custom', path: ['eindTijd'], message: 'De eindtijd moet na de begintijd liggen' })
  }
}

const planningVelden = {
  opleiderLocatieId: idParam('opleiderLocatieId').nullable(),
  voorkeurDatum: dagSchema,
  /**
   * Losse cursusdagen i.p.v. `aantalDagen` opeenvolgende werkdagen. De eerste
   * (vroegste) dag moet de voorkeursdatum zijn.
   */
  dagen: dagenSchema.optional(),
  tweedeVoorkeurDatum: dagSchema.nullable().optional(),
  zoSnelMogelijk: z.boolean().default(false),
  aantalDagen: z.number().int().min(1, 'Minimaal 1 dag').max(20, 'Maximaal 20 dagen').default(1),
  startTijd: tijdSchema.nullable().optional(),
  eindTijd: tijdSchema.nullable().optional(),
  toelichting: z.string().trim().max(2000).nullable().optional(),
}

function checkVoorkeuren(
  val: { voorkeurDatum: string; tweedeVoorkeurDatum?: string | null; dagen?: string[] },
  ctx: z.RefinementCtx,
): void {
  if (val.tweedeVoorkeurDatum && val.tweedeVoorkeurDatum === val.voorkeurDatum) {
    ctx.addIssue({
      code: 'custom',
      path: ['tweedeVoorkeurDatum'],
      message: 'De tweede voorkeur moet een andere dag zijn',
    })
  }
  checkDagenBeginOp(val.dagen, val.voorkeurDatum, 'dagen', ctx)
}

/** Losse dagen: de vroegste dag moet de (eerste) datum zijn. */
function checkDagenBeginOp(
  dagen: string[] | undefined,
  eersteDag: string | undefined,
  pad: string,
  ctx: z.RefinementCtx,
): void {
  if (!dagen || dagen.length === 0 || !eersteDag) return
  const vroegste = [...dagen].sort()[0]
  if (vroegste !== eersteDag) {
    ctx.addIssue({
      code: 'custom',
      path: [pad],
      message: 'De eerste cursusdag moet de gekozen datum zijn',
    })
  }
}

/** Stap 1-2: locatievoorstel voor opleiding x opleider x deelnemers. */
export const CursusAanvraagVoorstelSchema = z.object({
  opleidingId: idParam('opleidingId'),
  opleiderId: idParam('opleiderId').optional(),
  employeeIds: z.array(idParam('employeeId')).min(1, 'Kies minimaal één medewerker').max(50),
})
export type CursusAanvraagVoorstelInput = z.infer<typeof CursusAanvraagVoorstelSchema>

/** Stap 3: conflicten + klant-impact voor de voorkeur(en). */
export const CursusPlanningCheckSchema = z
  .object({
    employeeIds: z.array(idParam('employeeId')).min(1).max(50),
    voorkeurDatum: dagSchema,
    tweedeVoorkeurDatum: dagSchema.nullable().optional(),
    aantalDagen: z.number().int().min(1).max(20).default(1),
    /** Losse cursusdagen (zie `planningVelden.dagen`). */
    dagen: dagenSchema.optional(),
    startTijd: tijdSchema.nullable().optional(),
    eindTijd: tijdSchema.nullable().optional(),
    /** Zoek ook de eerste dag vanaf `voorkeurDatum` zonder conflicten ("zo snel mogelijk"). */
    zoekEersteVrijeDag: z.boolean().default(false),
    /** Bij aanpassen: de eigen sessie telt niet als conflict. */
    excludeSessieId: idParam('excludeSessieId').optional(),
  })
  .superRefine((v, ctx) => {
    checkTijden(v, ctx)
    checkDagenBeginOp(v.dagen, v.voorkeurDatum, 'dagen', ctx)
  })
export type CursusPlanningCheckInput = z.infer<typeof CursusPlanningCheckSchema>

/** Mailvoorbeeld voor een nieuwe aanvraag. */
export const CursusAanvraagMailPreviewSchema = z
  .object({
    opleidingId: idParam('opleidingId'),
    opleiderId: idParam('opleiderId'),
    entityId: idParam('entityId'),
    employeeIds: z.array(idParam('employeeId')).min(1).max(50),
    voorletters: z.record(z.string(), z.string().trim().max(20)).optional(),
    ...planningVelden,
  })
  .superRefine((v, ctx) => {
    checkTijden(v, ctx)
    checkVoorkeuren(v, ctx)
  })
export type CursusAanvraagMailPreviewInput = z.infer<typeof CursusAanvraagMailPreviewSchema>

/** Mailvoorbeeld bij een bestaande aanvraag (aanpassing of annulering). */
export const CursusAanvraagVervolgMailPreviewSchema = z.discriminatedUnion('soort', [
  z.object({
    soort: z.literal('AANPASSING'),
    ...planningVelden,
  }),
  z.object({
    soort: z.literal('ANNULERING'),
    reden: z.string().trim().max(2000).optional(),
  }),
])
export type CursusAanvraagVervolgMailPreviewInput = z.infer<
  typeof CursusAanvraagVervolgMailPreviewSchema
>

const mailVelden = {
  onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(300).optional(),
  body: z.string().trim().min(1, 'De mailtekst is verplicht').max(20000).optional(),
}

/**
 * De versie van de aanvraag zoals het scherm hem toonde. Heeft intussen iemand
 * anders besloten, dan weigert de API het besluit (409) in plaats van het over
 * het nieuwere besluit heen te leggen.
 */
const verwachteVersieSchema = z.number().int().min(0).optional()

/** Stap 4: aanvragen (+ direct voorlopig in de planning). */
export const CursusAanvraagCreateSchema = z
  .object({
    opleidingId: idParam('opleidingId'),
    opleiderId: idParam('opleiderId'),
    entityId: idParam('entityId'),
    employeeIds: z.array(idParam('employeeId')).min(1, 'Kies minimaal één medewerker').max(50),
    kanaal: z.enum(CURSUS_AANVRAAG_KANALEN),
    /** Werkbon ook aanmaken voor medewerkers met een conflict (planner past zelf aan). */
    planOndanksConflict: z.boolean().default(true),
    /** Gecorrigeerde voorletters per employeeId (ook voor vervolgmails). */
    voorletters: z.record(z.string(), z.string().trim().max(20)).optional(),
    bijlageIds: bijlageIdsSchema.optional(),
    ...planningVelden,
    ...mailVelden,
  })
  .superRefine((v, ctx) => {
    checkTijden(v, ctx)
    checkVoorkeuren(v, ctx)
    if (v.kanaal !== 'INTERN') {
      if (!v.onderwerp) ctx.addIssue({ code: 'custom', path: ['onderwerp'], message: 'Onderwerp is verplicht' })
      if (!v.body) ctx.addIssue({ code: 'custom', path: ['body'], message: 'De mailtekst is verplicht' })
    }
  })
export type CursusAanvraagCreateInput = z.infer<typeof CursusAanvraagCreateSchema>

/** Definitief maken (na akkoord). Zonder velden = de huidige planning. */
export const CursusAanvraagBevestigSchema = z
  .object({
    datum: dagSchema.optional(),
    /** Alle bevestigde cursusdagen (meerdaagse cursus met afwijkende dagen). */
    dagen: dagenSchema.optional(),
    startTijd: tijdSchema.nullable().optional(),
    eindTijd: tijdSchema.nullable().optional(),
    opleiderLocatieId: idParam('opleiderLocatieId').optional(),
    replyId: idParam('replyId').optional(),
    notitie: z.string().trim().max(2000).optional(),
    verwachteVersie: verwachteVersieSchema,
  })
  .superRefine((v, ctx) => {
    checkTijden(v, ctx)
    checkDagenBeginOp(v.dagen, v.datum, 'dagen', ctx)
  })
export type CursusAanvraagBevestigInput = z.infer<typeof CursusAanvraagBevestigSchema>

export const CursusAanvraagAfwijzingSchema = z.object({
  reden: z.string().trim().min(3, 'Geef de reden van de opleider op').max(2000),
  replyId: idParam('replyId').optional(),
  verwachteVersie: verwachteVersieSchema,
})
export type CursusAanvraagAfwijzingInput = z.infer<typeof CursusAanvraagAfwijzingSchema>

/** Aanpassen: nieuwe planning + nieuwe mail met DEZELFDE referentie. */
export const CursusAanvraagAanpassenSchema = z
  .object({
    ...planningVelden,
    ...mailVelden,
    replyId: idParam('replyId').optional(),
    planOndanksConflict: z.boolean().default(true),
    bijlageIds: bijlageIdsSchema.optional(),
    verwachteVersie: verwachteVersieSchema,
  })
  .superRefine((v, ctx) => {
    checkTijden(v, ctx)
    checkVoorkeuren(v, ctx)
  })
export type CursusAanvraagAanpassenInput = z.infer<typeof CursusAanvraagAanpassenSchema>

export const CursusAanvraagAnnulerenSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden op').max(2000),
  /** Annuleringsmail naar de opleider (alleen bij het mailkanaal). */
  mailOpleider: z.boolean().default(true),
  ...mailVelden,
  replyId: idParam('replyId').optional(),
  bijlageIds: bijlageIdsSchema.optional(),
  verwachteVersie: verwachteVersieSchema,
})
export type CursusAanvraagAnnulerenInput = z.infer<typeof CursusAanvraagAnnulerenSchema>

export const CursusAanvraagReplyKoppelSchema = z.object({
  aanvraagId: idParam('aanvraagId'),
})
export type CursusAanvraagReplyKoppelInput = z.infer<typeof CursusAanvraagReplyKoppelSchema>

/** "Geen actie nodig" / negeren - altijd met een notitie (audit). */
export const CursusAanvraagReplyAfhandelSchema = z.object({
  notitie: z.string().trim().min(3, 'Geef een korte toelichting').max(2000),
})
export type CursusAanvraagReplyAfhandelInput = z.infer<typeof CursusAanvraagReplyAfhandelSchema>

export const CursusAanvraagLijstQuerySchema = z.object({
  status: z.enum(CURSUS_AANVRAAG_STATUSSEN).optional(),
  /** Alleen open aanvragen (aangevraagd, te beoordelen, afgewezen). */
  open: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
  opleiderId: idParam('opleiderId').optional(),
  opleidingId: idParam('opleidingId').optional(),
  employeeId: idParam('employeeId').optional(),
  zoek: z.string().trim().max(100).optional(),
  vanaf: dagSchema.optional(),
  tot: dagSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type CursusAanvraagLijstQuery = z.infer<typeof CursusAanvraagLijstQuerySchema>

export const CursusAanvraagReplyLijstQuerySchema = z.object({
  status: z.enum(CURSUS_AANVRAAG_REPLY_STATUSSEN).optional(),
  /** Default: de werkvoorraad (te beoordelen, niet gekoppeld, fout, quarantaine). */
  alles: z
    .union([z.literal('true'), z.literal('false')])
    .transform((v) => v === 'true')
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type CursusAanvraagReplyLijstQuery = z.infer<typeof CursusAanvraagReplyLijstQuerySchema>

export const CursusAanvraagInstellingSchema = z.object({
  replyMailbox: z
    .string()
    .trim()
    .email('Ongeldig e-mailadres')
    .max(200)
    .nullable(),
  herinneringNaWerkdagen: z.number().int().min(1, 'Minimaal 1 werkdag').max(20),
  maxHerinneringen: z.number().int().min(0).max(5),
  signaalWerkdagenVooraf: z.number().int().min(0, 'Minimaal 0 werkdagen').max(10, 'Maximaal 10 werkdagen'),
})
export type CursusAanvraagInstellingInput = z.infer<typeof CursusAanvraagInstellingSchema>

export const CursusAanvraagSjabloonSchema = z.object({
  soort: z.enum(CURSUS_AANVRAAG_MAIL_SOORTEN),
  onderwerpSjabloon: z.string().trim().min(1, 'Onderwerp-sjabloon is verplicht').max(500),
  bodySjabloon: z.string().trim().min(1, 'Body-sjabloon is verplicht').max(20000),
  cursistBlokSjabloon: z.string().trim().min(1, 'Cursist-blok-sjabloon is verplicht').max(5000),
})
export type CursusAanvraagSjabloonInput = z.infer<typeof CursusAanvraagSjabloonSchema>

/** Contactadres bij de opleider (of de gekozen vestiging) vastleggen vanuit de wizard. */
export const CursusAanvraagContactadresSchema = z.object({
  opleiderId: idParam('opleiderId'),
  /** Gezet = het adres hoort bij deze vestiging; weg = bij de opleider zelf. */
  opleiderLocatieId: idParam('opleiderLocatieId').optional(),
  contactEmail: z.string().trim().email('Ongeldig e-mailadres').max(200),
})
export type CursusAanvraagContactadresInput = z.infer<typeof CursusAanvraagContactadresSchema>

// ─── Bijlagen ────────────────────────────────────────────────────────────────

/** Per bijlage (MailService-grens). */
export const CURSUS_AANVRAAG_BIJLAGE_MAX_BYTES = 4 * 1024 * 1024
/** Alle bijlagen van één mail samen (MailService-grens). */
export const CURSUS_AANVRAAG_BIJLAGEN_TOTAAL_MAX_BYTES = 10 * 1024 * 1024
export const CURSUS_AANVRAAG_MAX_BIJLAGEN = 5

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface CursusAanvraagDeelnemerKortDTO {
  employeeId: string
  naam: string
}

export interface CursusAanvraagLijstRijDTO {
  id: string
  referentie: string
  status: CursusAanvraagStatusKey
  kanaal: CursusAanvraagKanaalKey
  opleiding: { id: string; naam: string; code: string | null }
  opleider: { id: string; naam: string }
  locatie: { id: string; naam: string; plaats: string | null } | null
  deelnemers: CursusAanvraagDeelnemerKortDTO[]
  /** yyyy-MM-dd */
  voorkeurDatum: string
  /** Eerste cursusdag zoals nu in de planning (yyyy-MM-dd), null zonder sessie. */
  geplandeDatum: string | null
  /** ISO-tijdstip waarop de aanvraag verstuurd (of aangemaakt) is. */
  aangevraagdOp: string
  /** Kalenderdagen sinds het verzenden, in NL-tijd. */
  ouderdomDagen: number
  laatsteContactOp: string | null
  herinneringenVerzonden: number
  /** Antwoorden die op een besluit wachten. */
  openAntwoorden: number
  aangevraagdDoorNaam: string | null
}

export interface CursusAanvraagLijstMetaDTO {
  total: number
  page: number
  pageSize: number
  /** Tellingen per status over ALLE aanvragen (server-side, niet de pagina). */
  perStatus: Record<CursusAanvraagStatusKey, number>
  /** Opleidermails die aan geen aanvraag gekoppeld konden worden. */
  nietGekoppeldeMails: number
}

export interface CursusAanvraagBijlageDTO {
  id: string
  bestandsnaam: string
  mimeType: string
  /** Bytes. */
  grootte: number
}

/** Een meegestuurde bijlage; `magDownloaden` volgt dezelfde regel als de download-route. */
export interface CursusAanvraagMailBijlageDTO extends CursusAanvraagBijlageDTO {
  magDownloaden: boolean
}

export interface CursusAanvraagMailDTO {
  id: string
  soort: CursusAanvraagMailSoortKey | null
  methode: 'VERSTUURD' | 'GEKOPIEERD'
  ontvangerEmail: string
  ccEmail: string | null
  onderwerp: string
  /**
   * De verstuurde tekst; null voor wie geen privégegevens van medewerkers mag
   * zien (de tekst kan geboortedatums bevatten, AVG).
   */
  body: string | null
  bodyVerborgen: boolean
  mailStatus: string | null
  verzondenOp: string
  verzondenDoorNaam: string | null
  bijlagen: CursusAanvraagMailBijlageDTO[]
}

export interface CursusAanvraagReplyDTO {
  id: string
  status: CursusAanvraagReplyStatusKey
  afzender: string | null
  afzenderAdres: string | null
  onderwerp: string | null
  ontvangenOp: string | null
  referentie: string | null
  aanvraagId: string | null
  aanvraagReferentie: string | null
  matchWijze: CursusAanvraagMatchWijzeKey | null
  oordeel: CursusAanvraagReplyOordeelKey | null
  zekerheid: number | null
  citaat: string | null
  replyTekst: string | null
  voorgesteldeDatum: string | null
  /** Alle door de opleider genoemde cursusdagen (yyyy-MM-dd). */
  voorgesteldeDagen: string[]
  voorgesteldeStartTijd: string | null
  voorgesteldeEindTijd: string | null
  voorgesteldeLocatieTekst: string | null
  voorgesteldeLocatieId: string | null
  voorgesteldeLocatieNaam: string | null
  afwijzingsReden: string | null
  reviewReden: CursusAanvraagReviewRedenKey | null
  reviewToelichting: string | null
  afhandeling: string | null
  afgehandeldOp: string | null
  afgehandeldDoorNaam: string | null
  aantalBijlagen: number
  heeftPdf: boolean
  heeftEml: boolean
  /**
   * Het origineel (.eml) is voor deze gebruiker te openen: opgeslagen, door de
   * malwarescan en met recht op privégegevens (het citeert onze aanvraag).
   */
  origineelBeschikbaar: boolean
  foutmelding: string | null
}

export interface CursusAanvraagAuditDTO {
  id: string
  actie: string
  actieLabel: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

export interface CursusAanvraagWerkbonDTO {
  id: string
  werkbonNumber: string
  referentie: string | null
  medewerkerNaam: string | null
  onderVoorbehoud: boolean
  dagen: string[]
}

export interface CursusAanvraagSessieDTO {
  id: string
  status: string
  /** yyyy-MM-dd per effectieve cursusdag, met tijden. */
  dagen: Array<{ datum: string; startTijd: string | null; eindTijd: string | null }>
  locatie: string | null
}

export interface CursusAanvraagActiesDTO {
  magBevestigen: boolean
  magAfwijzingRegistreren: boolean
  magAanpassen: boolean
  magAnnuleren: boolean
  magHerinneren: boolean
}

export interface CursusAanvraagDetailDTO extends CursusAanvraagLijstRijDTO {
  entity: { id: string; naam: string } | null
  tweedeVoorkeurDatum: string | null
  zoSnelMogelijk: boolean
  aantalDagen: number
  startTijd: string | null
  eindTijd: string | null
  toelichting: string | null
  ontvangerEmail: string | null
  ccEmail: string | null
  verzondenOp: string | null
  bevestigdOp: string | null
  bevestigdDoorNaam: string | null
  afgewezenOp: string | null
  afwijzingsReden: string | null
  geannuleerdOp: string | null
  geannuleerdDoorNaam: string | null
  annuleringsReden: string | null
  opleiderInfo: {
    id: string
    naam: string
    contactEmail: string | null
    isIntern: boolean
    vereistGeboortedatum: boolean
  }
  /** Actieve vestigingen van de opleider (aanpassen/bevestigen). */
  locatieOpties: Array<{ id: string; naam: string; plaats: string | null }>
  deelnemersDetail: Array<{
    employeeId: string
    naam: string
    afstandKm: number | null
    deelnameStatus: string | null
  }>
  sessie: CursusAanvraagSessieDTO | null
  werkbonnen: CursusAanvraagWerkbonDTO[]
  mails: CursusAanvraagMailDTO[]
  replies: CursusAanvraagReplyDTO[]
  audit: CursusAanvraagAuditDTO[]
  acties: CursusAanvraagActiesDTO
  /** Komt het antwoord van de opleider automatisch binnen (reply-mailbox ingesteld)? */
  replyMailboxActief: boolean
  /**
   * De opleider had al bevestigd (ook als er daarna een antwoord ter beoordeling
   * kwam): afzeggen meldt het de deelnemers en de opleider krijgt altijd bericht.
   */
  bevestigdVoorheen: boolean
  /** Meegeven bij een besluit (`verwachteVersie`): voorkomt een besluit over een nieuwer besluit heen. */
  versie: number
  /**
   * De opleider had afgewezen (ook als er daarna een antwoord ter beoordeling
   * kwam): de planning is vrijgegeven, dus bevestigen plant de sessie opnieuw in.
   */
  afgewezenVoorheen: boolean
  planOndanksConflict: boolean
}

export type CursusLocatieAanbevelingBronKey =
  | 'AFSTAND'
  | 'OPLEIDING_DEFAULT'
  | 'PRIMAIRE_LOCATIE'
  | 'ENIGE_LOCATIE'
  | 'GEEN'

export interface CursusVoorstelLocatieDTO {
  locatieId: string
  naam: string
  plaats: string | null
  adres: string
  heeftCoordinaten: boolean
  isPrimair: boolean
  isOpleidingDefault: boolean
  /** Het mailadres waar de aanvraag naartoe gaat (vestiging, anders opleider). */
  contactEmail: string | null
  afstanden: Array<{ employeeId: string; afstandKm: number | null }>
  gemiddeldeKm: number | null
  maxKm: number | null
  aantalBekend: number
}

export type CursusVoorstelWaarschuwingCode =
  | 'AL_AANGEVRAAGD'
  | 'AL_GEPLAND'
  | 'GEEN_CONTACTADRES'
  | 'LOCATIE_ZONDER_COORDINATEN'
  | 'GEEN_LOCATIES'
  | 'GEEN_WOONADRES'
  | 'GEEN_OPLEIDER'
  | 'DEELNEMERS_VER_UITEEN'
  | 'GEEN_REPLY_MAILBOX'

export interface CursusVoorstelWaarschuwingDTO {
  code: CursusVoorstelWaarschuwingCode
  melding: string
  /** Relatieve link naar de plek waar het opgelost wordt (opleider-beheer). */
  link: string | null
}

export interface CursusAanvraagVoorstelDTO {
  opleiding: {
    id: string
    naam: string
    duurDagen: number | null
    defaultOpleiderId: string | null
    defaultLocatieId: string | null
  }
  opleider: {
    id: string
    naam: string
    isIntern: boolean
    contactEmail: string | null
    vereistGeboortedatum: boolean
  } | null
  /** Actieve opleiders (keuze als de cursus bij meerdere opleiders kan). */
  opleiderOpties: Array<{ id: string; naam: string; isIntern: boolean }>
  locaties: CursusVoorstelLocatieDTO[]
  aanbevolenLocatieId: string | null
  aanbevelingBron: CursusLocatieAanbevelingBronKey
  deelnemers: Array<{
    employeeId: string
    naam: string
    entityId: string | null
    entityNaam: string | null
    heeftWoonadres: boolean
  }>
  deelnemersZonderHerkomst: string[]
  locatiesZonderCoordinaten: string[]
  spreidingKm: number | null
  verUiteen: boolean
  splitsVoorstel: Array<{ locatieId: string; employeeIds: string[]; gemiddeldeKm: number | null }> | null
  /** Voorgestelde BV (die van de deelnemers als die gelijk is). */
  standaardEntityId: string | null
  waarschuwingen: CursusVoorstelWaarschuwingDTO[]
  replyMailboxActief: boolean
}

export interface CursusKlantImpactDTO {
  clientId: string
  klantNaam: string
  deelnemers: CursusAanvraagDeelnemerKortDTO[]
  dagen: Array<{ datum: string; startTijd: string | null; eindTijd: string | null }>
  werkbonnen: Array<{ werkbonId: string; werkbonNumber: string }>
}

export interface CursusPlanningCheckVoorkeurDTO {
  soort: 'EERSTE' | 'TWEEDE'
  dagen: string[]
  conflicten: Array<{
    employeeId: string
    naam: string
    conflicts: WerkbonConflictDTO[]
  }>
  klantImpact: CursusKlantImpactDTO[]
}

export interface CursusPlanningCheckDTO {
  voorkeuren: CursusPlanningCheckVoorkeurDTO[]
  /** Eerste dag (vanaf de voorkeur) waarop niemand een conflict heeft, of null. */
  eersteVrijeDag: string | null
}

export interface CursusAanvraagMailPreviewDTO {
  /** Het bewerkbare onderwerp (zonder referentie-token; dat zet de server erachter). */
  onderwerp: string
  body: string
  /** De referentie (bestaande aanvraag) of null (wordt bij verzenden toegekend). */
  referentie: string | null
  /** Het adres waar de mail heen gaat; null = er is er geen (handmatig kanaal). */
  ontvanger: string | null
  ontvangerBron: 'LOCATIE' | 'OPLEIDER' | null
  cc: string[]
  /** Waar het antwoord van de opleider binnenkomt (reply-to). */
  replyTo: string | null
  bedrijfsnaam: string
  cursisten: AanmeldCursistPreviewDTO[]
  privacyMasked: boolean
  isIntern: boolean
}

export interface CursusAanvraagCreateResultDTO {
  id: string
  referentie: string
  status: CursusAanvraagStatusKey
  kanaal: CursusAanvraagKanaalKey
  sessieId: string
  mailStatus: string | null
  /** Werkbon-generatie (onder voorbehoud, of definitief bij een interne opleider). */
  werkbonnen: { aangemaakt: number; overgeslagen: number; conflicten: number }
  waarschuwingen: string[]
  /** Handmatig kanaal: de vastgelegde tekst (referentie ingevuld) om zelf te versturen. */
  handmatig: { onderwerp: string; body: string; ontvanger: string | null } | null
}

/** Uitkomst van aanpassen/annuleren (meta naast de bijgewerkte detail). */
export interface CursusAanvraagMailUitkomstDTO {
  mailStatus: string | null
  /** Tekst om zelf te versturen (handmatig kanaal). */
  handmatig: { onderwerp: string; body: string; ontvanger: string | null } | null
  waarschuwingen: string[]
}

export interface CursusAanvraagHerinneringUitkomstDTO {
  mailStatus: string | null
  fout: string | null
}

export interface CursusAanvraagBevestigUitkomstDTO {
  waarschuwingen: string[]
}

export interface CursusCertificaatSignaalDTO {
  employeeId: string
  naam: string
  certificaatTypeId: string
  certificaatTypeNaam: string
  status: 'VERLOOPT_BINNENKORT' | 'VERLOPEN'
  /** yyyy-MM-dd, null als onbekend. */
  geldigTot: string | null
  dagenTotVerloop: number | null
  opleidingen: Array<{ id: string; naam: string }>
}

export interface CursusCertificaatSignalenDTO {
  signalen: CursusCertificaatSignaalDTO[]
  /** Totaal vóór afkappen (nooit stil afkappen). */
  totaal: number
  afgekapt: boolean
}

export interface CursusAanvraagInstellingDTO {
  replyMailbox: string | null
  /** De mailbox die nu echt gebruikt wordt (instelling, anders env). */
  effectieveReplyMailbox: string | null
  bron: 'INSTELLING' | 'ENV' | null
  herinneringNaWerkdagen: number
  maxHerinneringen: number
  signaalWerkdagenVooraf: number
  sjablonen: Record<CursusAanvraagMailSoortKey, CursusAanvraagSjabloon & { isDefault: boolean }>
}
